import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { fetchItinerary, addItineraryItem, removeItineraryItem } from '../api';

const SavedContext = createContext(null);

function getSid() {
  let sid = localStorage.getItem('uae_sid');
  if (!sid) {
    sid =
      (window.crypto && window.crypto.randomUUID && window.crypto.randomUUID()) ||
      's-' + Math.random().toString(36).slice(2) + Date.now().toString(36);
    localStorage.setItem('uae_sid', sid);
  }
  return sid;
}

export function SavedProvider({ children }) {
  const [sid] = useState(getSid);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    try {
      const data = await fetchItinerary(sid);
      setItems(data.items || []);
    } catch (e) {
      // keep existing items on error
    } finally {
      setLoading(false);
    }
  }, [sid]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const isSaved = useCallback(
    (refId) => items.some((i) => i.ref_id === refId),
    [items]
  );

  const toggle = useCallback(
    async (item) => {
      const existing = items.find((i) => i.ref_id === item.ref_id);
      if (existing) {
        setItems((prev) => prev.filter((i) => i.ref_id !== item.ref_id));
        try {
          await removeItineraryItem(sid, existing.id);
        } catch (e) {
          refresh();
        }
      } else {
        // optimistic
        const temp = { ...item, id: 'temp-' + item.ref_id };
        setItems((prev) => [...prev, temp]);
        try {
          const saved = await addItineraryItem(sid, item);
          setItems((prev) => prev.map((i) => (i.ref_id === item.ref_id ? saved : i)));
        } catch (e) {
          setItems((prev) => prev.filter((i) => i.ref_id !== item.ref_id));
        }
      }
    },
    [items, sid, refresh]
  );

  const remove = useCallback(
    async (itemId) => {
      setItems((prev) => prev.filter((i) => i.id !== itemId));
      try {
        await removeItineraryItem(sid, itemId);
      } catch (e) {
        refresh();
      }
    },
    [sid, refresh]
  );

  return (
    <SavedContext.Provider value={{ sid, items, count: items.length, isSaved, toggle, remove, loading }}>
      {children}
    </SavedContext.Provider>
  );
}

export function useSaved() {
  const ctx = useContext(SavedContext);
  if (!ctx) throw new Error('useSaved must be used within SavedProvider');
  return ctx;
}
